import { LinearGradient } from 'expo-linear-gradient';
import { memo, useCallback } from 'react';
import { type LayoutChangeEvent, Pressable, StyleSheet, Text, View } from 'react-native';
import { TouchableRipple } from 'react-native-paper';

import { StatusBadge } from '@/components/atoms/badge';
import { NotificationDot } from '@/components/atoms/indicator';
import { useTranslation } from '@/i18n';
import {
  HANDOFF_TYPE_TRANSLATION_KEYS,
  getHandoffCardDescription,
  type HandoffItem,
} from '@/models/handoffs';
import { colorPrimary, colorSemantic, radii, rippleOnSurface, spacing } from '@/theme';

export const URGENT_HANDOFF_CARD_WIDTH = 284;

const UNREAD_DOT_SIZE = 13;

interface UrgentHandoffCardProps {
  readonly item: HandoffItem;
  readonly isUpdating: boolean;
  /** Height of the tallest card in the carousel, so every card matches it. */
  readonly minHeight: number;
  readonly onMeasure: (height: number) => void;
  readonly onClaim: (handoffId: string) => void;
  readonly onPress?: (handoffId: string) => void;
}

const UrgentHandoffCardBase = ({
  item,
  isUpdating,
  minHeight,
  onMeasure,
  onClaim,
  onPress,
}: UrgentHandoffCardProps) => {
  const { t } = useTranslation();

  const handleClaim = useCallback(() => {
    onClaim(item.id);
  }, [item.id, onClaim]);

  const handlePress = useCallback(() => {
    onPress?.(item.id);
  }, [item.id, onPress]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onMeasure(event.nativeEvent.layout.height);
    },
    [onMeasure],
  );

  const description = getHandoffCardDescription({
    handoffType: item.handoffType,
    notes: item.notes,
    contactName: item.contact?.fullName,
    t,
  });

  return (
    <View
      style={[styles.shadow, minHeight > 0 ? { minHeight } : null]}
      testID={`urgent-handoff-card-${item.id}`}
      onLayout={handleLayout}>
      <LinearGradient
        colors={[colorPrimary.nl0, colorPrimary.nl300]}
        end={{ x: 0.5, y: 1 }}
        start={{ x: 0.5, y: 0 }}
        style={styles.card}>
        <TouchableRipple
          borderless
          accessibilityRole="button"
          rippleColor={rippleOnSurface}
          style={styles.pressable}
          testID={`urgent-handoff-card-${item.id}-body`}
          onPress={handlePress}>
          <View style={styles.content}>
            {description ? <Text style={styles.notes}>{description}</Text> : null}
            <View style={styles.badges}>
              <StatusBadge label={t('aiTasks.badge.unassignedTooLong')} variant="error" />
              <StatusBadge
                label={t(HANDOFF_TYPE_TRANSLATION_KEYS[item.handoffType])}
                style={styles.badgeSpacing}
                variant="info"
              />
            </View>
          </View>
        </TouchableRipple>
        <Pressable
          accessibilityRole="button"
          disabled={isUpdating}
          style={({ pressed }) => [
            styles.buttonPressable,
            pressed || isUpdating ? styles.buttonPressed : null,
          ]}
          testID={`urgent-handoff-card-${item.id}-claim`}
          onPress={handleClaim}>
          <LinearGradient
            colors={[colorPrimary.red200, colorPrimary.red300]}
            end={{ x: 0.5, y: 1 }}
            start={{ x: 0.5, y: 0 }}
            style={styles.button}>
            <Text style={styles.buttonLabel}>{t('aiTasks.action.claimAndOpen')}</Text>
          </LinearGradient>
        </Pressable>
      </LinearGradient>
      {item.unreadDispatch ? (
        <View pointerEvents="none" style={styles.unreadDot}>
          <NotificationDot
            borderColor={colorPrimary.nl0}
            borderWidth={2}
            color={colorSemantic.colorBgErrorStrong}
            size={UNREAD_DOT_SIZE}
            testID={`urgent-handoff-card-${item.id}-unread-dot`}
          />
        </View>
      ) : null}
    </View>
  );
};

export const UrgentHandoffCard = memo(UrgentHandoffCardBase);

const styles = StyleSheet.create({
  pressable: {
    borderRadius: radii.md,
    flex: 1,
  },
  // The 13px dot straddles the card's top-left corner, 4px left and 4px above
  // the edge.
  unreadDot: {
    left: -4,
    position: 'absolute',
    top: -4,
    zIndex: 1,
  },
  shadow: {
    width: URGENT_HANDOFF_CARD_WIDTH,
    shadowColor: '#d01616',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 4.5,
    elevation: 4,
  },
  card: {
    flex: 1,
    borderColor: colorSemantic.colorBorderErrorWeak,
    borderRadius: radii.md,
    borderWidth: 1,
    padding: spacing[4],
    rowGap: spacing[6],
  },
  content: {
    rowGap: spacing[2],
  },
  notes: {
    color: colorSemantic.colorTextErrorBase,
    fontSize: 14,
    fontWeight: '500',
    letterSpacing: 0.1,
    lineHeight: 20,
  },
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: spacing[1],
  },
  badgeSpacing: {
    marginLeft: spacing[1],
  },
  buttonPressable: {
    alignSelf: 'flex-start',
    // Keeps the action pinned to the bottom so buttons line up across cards.
    marginTop: 'auto',
    borderRadius: radii.sm,
    overflow: 'hidden',
  },
  buttonPressed: {
    opacity: 0.6,
  },
  button: {
    alignItems: 'center',
    borderColor: colorSemantic.colorBorderErrorStrong,
    borderRadius: radii.sm,
    borderWidth: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[1],
  },
  buttonLabel: {
    color: colorPrimary.nl0,
    fontSize: 11,
    letterSpacing: 0.4,
    lineHeight: 16,
    textAlign: 'center',
  },
});
